import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Save } from "lucide-react";
import {
  listEmailSettings,
  saveEmailSettings,
  saveSubjectOverride,
} from "@/lib/email-settings.functions";

type Environment = "preview" | "production";

export const Route = createFileRoute("/_authenticated/admin/email-instellingen")({
  head: () => ({
    meta: [
      { title: "Admin — E-mail instellingen" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EmailSettingsPage,
});

function EmailSettingsPage() {
  const list = useServerFn(listEmailSettings);
  const save = useServerFn(saveEmailSettings);
  const saveSubject = useServerFn(saveSubjectOverride);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["email-settings"],
    queryFn: () => list(),
  });

  const saveMut = useMutation({
    mutationFn: (input: Parameters<typeof save>[0]["data"]) => save({ data: input }),
    onSuccess: () => {
      toast.success("Afzender opgeslagen");
      qc.invalidateQueries({ queryKey: ["email-settings"] });
    },
    onError: (e: any) => toast.error(e.message || "Opslaan mislukt"),
  });

  const subjectMut = useMutation({
    mutationFn: (input: Parameters<typeof saveSubject>[0]["data"]) =>
      saveSubject({ data: input }),
    onSuccess: () => {
      toast.success("Onderwerp opgeslagen");
      qc.invalidateQueries({ queryKey: ["email-settings"] });
    },
    onError: (e: any) => toast.error(e.message || "Opslaan mislukt"),
  });

  const [env, setEnv] = useState<Environment>("production");

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <p className="text-muted-foreground">Laden…</p>
      </div>
    );
  }

  const current =
    data.settings.find((s: any) => s.environment === env) || {
      environment: env,
      site_name: "",
      from_local: "noreply",
      from_domain: "",
      sender_domain: "",
      reply_to: "",
    };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 space-y-8">
      <header className="space-y-2">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Mail className="size-6" /> E-mail instellingen
        </h1>
        <p className="text-sm text-muted-foreground">
          Configureer afzender en onderwerpen per omgeving. Wijzigingen zijn direct
          actief bij de volgende verzending.
        </p>
      </header>

      <div className="inline-flex rounded-md border border-border bg-muted p-1">
        {(["production", "preview"] as Environment[]).map((e) => (
          <button
            key={e}
            onClick={() => setEnv(e)}
            className={`rounded px-4 py-1.5 text-sm font-medium transition ${
              env === e ? "bg-white shadow" : "text-muted-foreground"
            }`}
          >
            {e === "production" ? "Productie" : "Preview"}
          </button>
        ))}
      </div>

      <SenderForm
        key={env}
        env={env}
        initial={current}
        onSave={(vals) => saveMut.mutate({ environment: env, ...vals })}
        saving={saveMut.isPending}
      />

      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold">Onderwerpen per template</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Laat leeg om het standaardonderwerp uit de code te gebruiken.
        </p>
        <div className="space-y-3">
          {data.templates.map((tpl: any) => {
            const override = data.overrides.find(
              (o: any) => o.environment === env && o.template_name === tpl.name
            );
            return (
              <SubjectRow
                key={`${env}-${tpl.name}`}
                template={tpl}
                initialSubject={override?.subject || ""}
                onSave={(subject) =>
                  subjectMut.mutate({
                    environment: env,
                    template_name: tpl.name,
                    subject: subject || null,
                  })
                }
              />
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SenderForm({
  env,
  initial,
  onSave,
  saving,
}: {
  env: Environment;
  initial: any;
  onSave: (v: {
    site_name: string;
    from_local: string;
    from_domain: string;
    sender_domain: string;
    reply_to: string | null;
  }) => void;
  saving: boolean;
}) {
  const [siteName, setSiteName] = useState(initial.site_name || "");
  const [fromLocal, setFromLocal] = useState(initial.from_local || "noreply");
  const [fromDomain, setFromDomain] = useState(initial.from_domain || "");
  const [senderDomain, setSenderDomain] = useState(initial.sender_domain || "");
  const [replyTo, setReplyTo] = useState(initial.reply_to || "");

  return (
    <section className="rounded-lg border border-border bg-white p-6 space-y-4">
      <h2 className="text-lg font-semibold">
        Afzender ({env === "production" ? "Productie" : "Preview"})
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Afzendernaam">
          <input
            className="w-full rounded border border-border px-3 py-2 text-sm"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="Elvion Group Nederland"
          />
        </Field>
        <Field label="Reply-to (optioneel)">
          <input
            className="w-full rounded border border-border px-3 py-2 text-sm"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="info@elvion-group.nl"
          />
        </Field>
        <Field label="From — lokaal deel">
          <input
            className="w-full rounded border border-border px-3 py-2 text-sm font-mono"
            value={fromLocal}
            onChange={(e) => setFromLocal(e.target.value)}
            placeholder="noreply"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Verschijnt vóór @ in de From-header.
          </p>
        </Field>
        <Field label="From — domein">
          <input
            className="w-full rounded border border-border px-3 py-2 text-sm font-mono"
            value={fromDomain}
            onChange={(e) => setFromDomain(e.target.value)}
            placeholder="elvion-group.nl"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Zichtbaar domein in de From-header.
          </p>
        </Field>
        <Field label="Sender-domein (verificatie)">
          <input
            className="w-full rounded border border-border px-3 py-2 text-sm font-mono"
            value={senderDomain}
            onChange={(e) => setSenderDomain(e.target.value)}
            placeholder="notify.elvion-group.nl"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Het gedelegeerde subdomein (met DNS/DKIM).
          </p>
        </Field>
      </div>

      <div className="pt-2 text-sm text-muted-foreground">
        <span className="font-medium">Preview van From-header:</span>{" "}
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
          {siteName} &lt;{fromLocal}@{fromDomain}&gt;
        </code>
      </div>

      <button
        onClick={() =>
          onSave({
            site_name: siteName,
            from_local: fromLocal,
            from_domain: fromDomain,
            sender_domain: senderDomain,
            reply_to: replyTo || null,
          })
        }
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-md bg-orange px-4 py-2 text-sm font-medium text-white hover:bg-orange/90 disabled:opacity-50"
      >
        <Save className="size-4" />
        {saving ? "Opslaan…" : "Afzender opslaan"}
      </button>
    </section>
  );
}

function SubjectRow({
  template,
  initialSubject,
  onSave,
}: {
  template: { name: string; displayName: string; defaultSubject: string };
  initialSubject: string;
  onSave: (subject: string) => void;
}) {
  const [value, setValue] = useState(initialSubject);
  useEffect(() => setValue(initialSubject), [initialSubject]);

  const dirty = value !== initialSubject;

  return (
    <div className="grid gap-2 rounded-md border border-border p-3 sm:grid-cols-[1fr_2fr_auto] sm:items-center">
      <div>
        <div className="text-sm font-medium">{template.displayName}</div>
        <div className="font-mono text-xs text-muted-foreground">{template.name}</div>
      </div>
      <input
        className="w-full rounded border border-border px-3 py-2 text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={template.defaultSubject}
      />
      <button
        onClick={() => onSave(value)}
        disabled={!dirty}
        className="rounded-md border border-border bg-white px-3 py-2 text-xs font-medium hover:bg-muted disabled:opacity-40"
      >
        Opslaan
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}
